import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, ShieldAlert, AlertTriangle, Trash2, Plus, Eye, EyeOff } from "lucide-react";

const REASONS = [
    "Mosrespektim i rregullave të klasës",
    "Mungesa të pajustifikuara të vazhdueshme",
    "Sjellje agresive",
    "Përdorim i telefonit gjatë mësimit",
    "Dëmtim i pronës së shkollës",
    "Tjetër",
];

const SeverityChip = ({ severity }) => severity === "warning" ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-bold">
        <AlertTriangle className="w-3 h-3" /> Vërejtje
    </span>
) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-terracotta/10 text-terracotta border border-terracotta/30 text-[11px] font-bold">
        <ShieldAlert className="w-3 h-3" /> Pezullim
    </span>
);

export default function KujdestariDiscipline() {
    const [params] = useSearchParams();
    const { user } = useAuth();
    const [classId, setClassId] = useState(params.get("class_id") || "");
    const [classes, setClasses] = useState([]);
    const [students, setStudents] = useState([]);
    const [statusMap, setStatusMap] = useState({});
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ student_id: "", severity: "warning", reason: REASONS[0], custom_reason: "", note: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [expandedId, setExpandedId] = useState(null);
    const [studentDetails, setStudentDetails] = useState({});

    const refresh = async () => {
        try {
            const c = await api.get("/kujdestari/my-classes");
            setClasses(c.data);
            const cid = classId || c.data[0]?.id;
            if (!cid) return;
            setClassId(cid);
            const s = await api.get("/teacher/students", { params: { class_id: cid } });
            setStudents(s.data);
            // Fetch status per student
            const statuses = await Promise.all(s.data.map((st) => api.get(`/students/${st.id}/discipline-status`).then((r) => [st.id, r.data])));
            setStatusMap(Object.fromEntries(statuses));
        } catch { /* ignore */ }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [classId]);

    const loadDetails = async (sid) => {
        if (studentDetails[sid]) {
            setExpandedId(expandedId === sid ? null : sid);
            return;
        }
        try {
            const r = await api.get(`/students/${sid}/discipline`);
            setStudentDetails((m) => ({ ...m, [sid]: r.data }));
            setExpandedId(sid);
        } catch (e) {
            alert("Gabim: " + (e.response?.data?.detail || e.message));
        }
    };

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            const reason = form.reason === "Tjetër" ? form.custom_reason : form.reason;
            if (!reason.trim()) { setErr("Arsyeja është e detyrueshme"); setBusy(false); return; }
            await api.post("/teacher/discipline", {
                student_id: form.student_id, severity: form.severity, reason, note: form.note,
            });
            setOpen(false);
            setForm({ student_id: "", severity: "warning", reason: REASONS[0], custom_reason: "", note: "" });
            // Re-load details for this student
            setStudentDetails((m) => { const n = { ...m }; delete n[form.student_id]; return n; });
            refresh();
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };

    const del = async (rid, sid) => {
        if (!window.confirm("Hiq këtë masë?")) return;
        await api.delete(`/teacher/discipline/${rid}`);
        setStudentDetails((m) => { const n = { ...m }; delete n[sid]; return n; });
        refresh();
    };

    return (
        <Layout>
            <Link to="/kujdestari" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Disiplina</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Vërejtje & Pezullime</h1>
                    <p className="text-slate-600 mt-2 text-sm">3 vërejtje = karton i verdhë • 2 pezullime = drejt përjashtimit</p>
                </div>
                <div className="flex gap-2">
                    {classes.length > 1 && (
                        <select value={classId} onChange={(e) => setClassId(e.target.value)} className="h-11 rounded-md border border-slate-300 bg-white text-sm px-3 font-semibold" data-testid="disc-class-select">
                            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    )}
                    <Button onClick={() => setOpen(true)} className="bg-terracotta hover:bg-terracotta/90 text-white gap-2" data-testid="disc-new-btn">
                        <Plus className="w-4 h-4" /> Shto masë
                    </Button>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/50 text-[10px] uppercase tracking-widest font-bold text-slate-700">
                    Nxënësit e klasës ({students.length})
                </div>
                <div className="divide-y divide-slate-200">
                    {students.map((s) => {
                        const st = statusMap[s.id] || { warnings: 0, suspensions: 0 };
                        const yellow = st.yellow_card;
                        const red = st.red_card_eligible;
                        const isOpen = expandedId === s.id;
                        return (
                            <div key={s.id} data-testid={`disc-student-${s.id}`}>
                                <button onClick={() => loadDetails(s.id)} className="w-full text-left px-4 sm:px-5 py-3 flex items-center gap-3 hover:bg-bone/30">
                                    <div className="w-9 h-9 rounded-xl bg-oxford/10 text-oxford flex items-center justify-center font-bold text-xs shrink-0">
                                        {s.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-slate-900 truncate text-sm">{s.name}</span>
                                            {yellow && <span className="inline-flex items-center justify-center w-4 h-5 rounded-sm bg-amber-400" title="Karton i verdhë (3+ vërejtje)" data-testid={`yellow-card-${s.id}`}></span>}
                                            {red && <span className="inline-flex items-center justify-center w-4 h-5 rounded-sm bg-terracotta" title="Drejt përjashtimit (2+ pezullime)" data-testid={`red-card-${s.id}`}></span>}
                                        </div>
                                        <div className="text-[11px] text-slate-500">
                                            {st.warnings > 0 && <span className="text-amber-700">⚠ {st.warnings} vërejtje</span>}
                                            {st.suspensions > 0 && <span className="ml-2 text-terracotta">🟥 {st.suspensions} pezullime</span>}
                                            {st.warnings === 0 && st.suspensions === 0 && <span>Asnjë masë</span>}
                                        </div>
                                    </div>
                                    {isOpen ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                                </button>
                                {isOpen && studentDetails[s.id] && (
                                    <div className="px-4 sm:px-5 pb-3 pl-16 space-y-2 bg-bone/20">
                                        {studentDetails[s.id].length === 0 ? (
                                            <div className="text-xs text-slate-500 italic">Pa masa.</div>
                                        ) : studentDetails[s.id].map((d) => (
                                            <div key={d.id} className="bg-white border border-slate-200 rounded-lg p-3 flex items-start gap-3" data-testid={`disc-row-${d.id}`}>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <SeverityChip severity={d.severity} />
                                                        <span className="text-[10px] text-slate-400 font-mono">{d.created_at?.slice(0, 10)}</span>
                                                    </div>
                                                    <p className="text-xs text-slate-700 leading-relaxed">{d.reason}</p>
                                                    {d.note && <p className="text-[11px] text-slate-500 mt-1 italic">{d.note}</p>}
                                                    <div className="text-[10px] text-slate-400 mt-1">— {d.created_by_name}</div>
                                                </div>
                                                {(user?.role === "super_admin" || d.created_by === user?.id) && (
                                                    <button onClick={() => del(d.id, s.id)} className="text-terracotta hover:bg-terracotta/10 p-1 rounded shrink-0" data-testid={`disc-del-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Shto masë disiplinore</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-3">
                        <select required value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-3" data-testid="disc-form-student">
                            <option value="">— Nxënësi —</option>
                            {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-2">Lloji i masës</label>
                            <div className="grid grid-cols-2 gap-2">
                                <button type="button" onClick={() => setForm({ ...form, severity: "warning" })}
                                    className={`px-3 py-3 rounded-md border-2 text-center transition ${form.severity === "warning" ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-amber-300"}`}
                                    data-testid="disc-form-warning">
                                    <AlertTriangle className="w-5 h-5 mx-auto text-amber-600 mb-1" />
                                    <div className="text-sm font-bold">Vërejtje</div>
                                    <div className="text-[10px] text-slate-500">Çdo mësues mund të krijojë</div>
                                </button>
                                <button type="button" onClick={() => setForm({ ...form, severity: "suspension" })}
                                    className={`px-3 py-3 rounded-md border-2 text-center transition ${form.severity === "suspension" ? "border-terracotta bg-terracotta/5" : "border-slate-200 hover:border-terracotta/50"}`}
                                    data-testid="disc-form-suspension">
                                    <ShieldAlert className="w-5 h-5 mx-auto text-terracotta mb-1" />
                                    <div className="text-sm font-bold">Pezullim</div>
                                    <div className="text-[10px] text-slate-500">Vetëm kujdestar / admin</div>
                                </button>
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1">Arsyeja</label>
                            <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-3" data-testid="disc-form-reason">
                                {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                            {form.reason === "Tjetër" && (
                                <Input value={form.custom_reason} onChange={(e) => setForm({ ...form, custom_reason: e.target.value })} placeholder="Përshkruaj arsyen…" className="mt-2" data-testid="disc-form-custom" />
                            )}
                        </div>
                        <Textarea placeholder="Shënim opsional" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} data-testid="disc-form-note" />
                        {err && <div className="text-xs text-terracotta">{err}</div>}
                        <DialogFooter>
                            <Button type="submit" disabled={busy} className="bg-terracotta hover:bg-terracotta/90 text-white" data-testid="disc-form-submit">{busy ? "Duke ruajtur…" : "Krijo masën"}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
